import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Input } from 'reactstrap';
import SimpleBar from "simplebar-react";
import axios from "axios";

import { useAuth } from '../../Provider/AuthProvider';
import { useLocation } from 'react-router-dom';
import { ApiResponse } from './Interfaces';

interface SearchResult {
    id: string;
    ref: string;
    client: string;
    total_ht: string;
    total_ttc: string;
    date: string;
    status: string;
    type: string;
    invoicepdf: string;
    resultPDF: string;
}

type SearchOptionHandle = {
    setSearchTermRef: (term: string) => void;
};


const SearchOrderByTerm = forwardRef<SearchOptionHandle>((props, ref) => {
    const { user, order, changeOrder, changeProduct, RefProduct, setRefProduct, setLoading, setBrands, setSelectedBrands } = useAuth();

    const [searchTerm, setSearchTerm] = useState<string>(order ? order.ref : '');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const location = useLocation();


    useEffect(() => {
        setSearchTerm(order?.ref ?? '');
        setSearchResults([]);
        const dropdown = document.getElementById("search-dropdown") as HTMLElement;
        const searchOptions = document.getElementById("search-close-options") as HTMLElement;
        if (dropdown && searchOptions && !order) {
            dropdown.classList.remove("show");
            searchOptions.classList.add("d-none");
        }
    }, [order]);

    useEffect(() => {
        if (location.pathname !== "/caisse") return;

        const searchInput = document.getElementById("search-options") as HTMLInputElement | null;
        const searchOptions = document.getElementById("search-close-options") as HTMLElement | null;
        const dropdown = document.getElementById("search-dropdown") as HTMLElement | null;

        if (!searchInput || !searchOptions || !dropdown) return;

        const handleSearchInput = () => {
            const inputLength = searchInput.value.length;
            if (inputLength > 0) {
                dropdown.classList.add("show");
                searchOptions.classList.remove("d-none");
            } else {
                dropdown.classList.remove("show");
                searchOptions.classList.add("d-none");
            }
        };

        const handleClickClear = () => {
            searchInput.value = "";
            dropdown.classList.remove("show");
            searchOptions.classList.add("d-none");
            setSearchTerm("");
            changeOrder(null);
        };

        const handleBodyClick = (e: MouseEvent) => {
            if (!(e.target as HTMLElement)?.closest("#search-options")) {
                dropdown.classList.remove("show");
                searchOptions.classList.add("d-none");
            }
        };

        searchInput.addEventListener("focus", handleSearchInput);
        searchInput.addEventListener("keyup", handleSearchInput);
        searchOptions.addEventListener("click", handleClickClear);
        document.body.addEventListener("click", handleBodyClick);

        return () => {
            searchInput.removeEventListener("focus", handleSearchInput);
            searchInput.removeEventListener("keyup", handleSearchInput);
            searchOptions.removeEventListener("click", handleClickClear);
            document.body.removeEventListener("click", handleBodyClick);
        };
    }, [location.pathname]);


    const handleSelectResult = (result: SearchResult) => {
        console.log("Search result resultPDF:", result.resultPDF); 

        const orderWithInvoicePdf = {
            ...result,
            invoicepdf: result.resultPDF 
        };

        setSearchTerm(result.ref);
        setSearchResults([]);
        changeOrder(orderWithInvoicePdf); 
        if (inputRef.current) {
            inputRef.current.blur();
        }
        const dropdown = document.getElementById("search-dropdown") as HTMLElement;
        const searchOptions = document.getElementById("search-close-options") as HTMLElement;
        dropdown.classList.remove("show");
        searchOptions.classList.add("d-none");
    };
    const onKeyDownPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();

            if (location.pathname === '/vendeur') {
                handleSearch();
            }
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const value = event.target.value;
        setSearchTerm(value);
        if (value === "") {
            setSearchResults([]);
        }
    };

    
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm.length >= 3) {
                setIsSearchLoading(true);
                axios
                    .post(
                        "/apiV2/",
                        {
                            action: "search",
                            term: searchTerm,
                            entity: user.entity,
                        },
                        {
                            baseURL: "https://api.apapiecesauto.fr",
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${user.token}`,
                            },
                        }
                    ) 
                    .then((response) => {
                        
                        setSearchResults(response.data || []);
                        
                        console.log(searchResults)

                    })
                    .catch((error) => {
                        console.error("Erreur lors de la recherche:", error);
                        setSearchResults([]);
                    })
                    .finally(() => {
                        setIsSearchLoading(false);
                    });
            } else {
                setSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, user.entity]);

    const handleSearch = async () => {
        if (!RefProduct.trim()) return;
        setBrands({});
        changeProduct([]);
        setSelectedBrands([]);
        setLoading(true);
        
        try {
            const savedCart = localStorage.getItem('cart');
            const cart = savedCart ? JSON.parse(savedCart) : { client: '', items: [], clientInfo: null };
            const clientId = cart?.clientInfo?.rowid;

            const response: any = await axios.post<ApiResponse>(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'ref',
                    entity: user.entity,
                    reference: RefProduct,
                    clientId : clientId || "",
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            changeProduct(response.r || []);
            setBrands(response.brands || {});
        } catch (error) {
            console.error('Error fetching data:', error);
            if (axios.isAxiosError(error)) {
                console.error('Axios error details:', error.response?.data);
            }
        } finally {
            setLoading(false);
        }
    };



    return (
        <React.Fragment>
            <form className="app-search d-none d-md-block">
                {location.pathname === '/caisse' && (
                    <>
                        <div className="position-relative dropdown-header m-3">
                            <Input
                                type="text"
                                className="form-control"
                                style={{ width: '260px' }}
                                placeholder="Rechercher une commande"
                                id="search-options"handl
                                value={searchTerm}
                                onKeyDown={onKeyDownPress}
                                onChange={handleChange}
                                innerRef={inputRef}
                                autoComplete="off"
                            />
                     
                            <span className="mdi mdi-magnify search-widget-icon"></span>
                            <span
                                className="mdi mdi-close-circle search-widget-icon search-widget-icon-close d-none"
                                id="search-close-options"
                                onClick={() => {
                                    setSearchTerm('');
                                    setSearchResults([]);
                                    changeOrder(null);
                                    const dropdown = document.getElementById("search-dropdown") as HTMLElement;
                                    const searchOptions = document.getElementById("search-close-options") as HTMLElement;
                                    if (dropdown && searchOptions) {
                                        dropdown.classList.remove("show");
                                        searchOptions.classList.add("d-none");
                                    }
                                }}
                            ></span>
                        </div>
                        <div className="m-3">
                            <div className="dropdown-menu dropdown-menu-lg" id="search-dropdown" style={{ width: "260px" }}>
                                <SimpleBar style={{ height: "320px" }}>
                                    {searchTerm.length >= 3 && (
                                        <>
                                            <div className="dropdown-header">
                                                <h6 className="text-overflow text-muted mb-0 text-uppercase">Résultats de la recherche</h6>
                                            </div>
                                            {isSearchLoading ? (
                                                <div className="dropdown-item py-2 text-muted">
                                                    <div className="d-flex">
                                                        <div className="flex-grow-1">
                                                            <h6 className="m-0">Recherche en cours...</h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : searchResults.length > 0 ? (
                                                searchResults.map((result, index) => (
                                                    <div
                                                        key={index}
                                                        className="dropdown-item py-2"
                                                        onClick={() => handleSelectResult(result)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <div className="flex-grow-1">
                                                                <h6 className="m-0">{result.ref}</h6>
                                                                <span className="fs-11 mb-0 text-muted">{result.client}</span>
                                                            </div>
                                                            <div className="d-flex flex-column align-items-end">
                                                                <span className="fs-11 mb-1 text-muted">{result.date}</span>
                                                                <span
                                                                    className={`badge ${result.type === "commande" ? "bg-success" : "bg-warning"}`}
                                                                >
                                                                    {result.type}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="dropdown-item py-2 text-muted">
                                                    <div className="d-flex">
                                                        <div className="flex-grow-1">
                                                            <h6 className="m-0">Aucun résultat trouvé</h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </SimpleBar>
                            </div>
                        </div>
                    </>
                )}
                {location.pathname === '/vendeur' && (
                    <div className="position-relative dropdown-header m-3 d-flex gap-2">
                        <Input
                            type="text"
                            className="form-control"
                            style={{ width: '260px' }}
                            placeholder="Rechercher par référence"
                            value={RefProduct}
                            onKeyDown={onKeyDownPress}
                            onChange={(e) => setRefProduct(e.target.value)}
                        />
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSearch} 
                        >
                            <i className="ri ri-search-line me-2" />Rechercher
                        </button>
                        <span
                            className="mdi mdi-close-circle search-widget-icon search-widget-icon-close d-none"
                            id="search-close-options-ref"
                            onClick={() => {
                                setRefProduct('');
                                changeOrder(null);
                            }}
                        ></span>
                    </div>
                )}

            </form>
        </React.Fragment>
    );
});

export default SearchOrderByTerm;